// src/components/circles/CircleVoiceCallModal.tsx
//
// Phase 12.9 — Circle-native raw-number dialer.
//
// Renders on Circle → People, on the CURRENT authenticated user's own row.
// The target is a raw dialable phone number. NO Contact search, NO Contact
// creation, NO Contact identity attached. This surface is Central-user +
// Circle only.
//
// Reuse contract:
//   * Reuses the shared Twilio Device via `useTwilioVoiceDevice()`.
//   * Reuses `voice.switchProvider(outboundProviderId)` — the existing
//     per-provider re-mint + rebuild path. That path calls
//     `POST /api/telephony/voice/token` which enforces the caller's
//     existing `phone.call` capability.
//   * Reuses `voice.connectToNumber(phone)` — the existing outbound
//     dial primitive.
//   * The active call renders in the reused `ActiveCallShell` (mounted
//     alongside the modal by the parent surface).
//
// Two-step token flow:
//   1. Mint the Circle voice token via `POST /api/circles/:id/voice/token`
//      (Phase 12.8). Server derives the runtime user from `req.user.id`
//      and resolves the member-Circle pin OR falls through to workspace
//      default. Response echoes the resolved `outboundProviderId` so we
//      never lie about which provider fires.
//   2. When an `outboundProviderId` is returned, call `switchProvider`
//      to bind the Device to that provider. If the endpoint returned
//      no pin (workspace fallback), leave the Device on its current
//      workspace-default binding — no switch needed.
//   3. `connectToNumber(phone)` places the call.
//
// The modal only closes on user action (Cancel) or successful call
// initiation. Suspended-provider fall-through surfaces the resolved
// provider so the user sees what actually fired.

import { useMemo, useState } from 'react';
import { useMintCircleVoiceTokenMutation } from '../../api/circlesApi';
import { useTwilioVoiceDevice } from '../../hooks/useTwilioVoiceDevice';
import { readEntitlementError } from '../../utils/entitlementErrors';

interface CircleVoiceCallModalProps {
  circleId: string;
  circleName: string;
  /**
   * The assigned voice-provider display name from the current row's chip,
   * if any. Used ONLY as an initial hint above the dial input. The final
   * resolved provider comes from the mint response and can differ (pin
   * suspended → workspace default).
   */
  assignedProviderHint?: string | null;
  /**
   * The id behind `assignedProviderHint`, so the resolved provider id from
   * the mint can be compared with the pin. Ids are never shown to the user.
   */
  assignedProviderId?: string | null;
  onClose: () => void;
}

// Simple, dependency-free E.164 validator.
//   + <country>(1-9) then 6..14 more digits → 7..15 total (matches ITU E.164).
// Deliberately does NOT try to be libphonenumber — the Twilio side and the
// server will do a stricter parse; this just blocks obvious garbage before
// we touch the network.
const E164 = /^\+[1-9]\d{6,14}$/;

function normalizeCandidate(raw: string): string {
  return raw.trim().replace(/[\s()\-.]/g, '');
}

export default function CircleVoiceCallModal({
  circleId,
  circleName,
  assignedProviderHint,
  assignedProviderId = null,
  onClose,
}: CircleVoiceCallModalProps) {
  const [phoneInput, setPhoneInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resolvedProviderId, setResolvedProviderId] = useState<string | null>(
    null,
  );
  const [placing, setPlacing] = useState(false);

  const [mintCircleVoiceToken] = useMintCircleVoiceTokenMutation();
  const voice = useTwilioVoiceDevice();

  const normalizedPhone = useMemo(
    () => normalizeCandidate(phoneInput),
    [phoneInput],
  );
  const phoneIsValid = E164.test(normalizedPhone);

  const canPlace =
    !placing && phoneIsValid && voice.isReady && voice.activeCall.kind === 'idle';

  const handlePlace = async () => {
    setError(null);
    if (!phoneIsValid) {
      setError(
        'Enter the destination phone number in E.164 format, e.g. +15551234567.',
      );
      return;
    }
    setPlacing(true);
    try {
      // Step 1 — mint the Circle voice token. Server derives userId.
      const mint = await mintCircleVoiceToken({ circleId }).unwrap();
      setResolvedProviderId(mint.outboundProviderId);

      // Step 2 — bind the shared Device to the resolved provider. When
      // the pin was absent or invalid, Outbound already fell through to
      // workspace default and returned that provider id. Either way,
      // switchProvider brings the Device to the SAME account the token
      // was minted against. If Outbound returned no provider id, the
      // workspace-default token is already good enough and switchProvider
      // would fail (no id to switch to) — skip the switch in that case.
      if (mint.outboundProviderId) {
        const switched = await voice.switchProvider(mint.outboundProviderId);
        if (!switched.ok) {
          setError(
            switched.message ||
              'Voice device could not be rebuilt for this Circle provider.',
          );
          setPlacing(false);
          return;
        }
      }

      // Step 3 — reuse the existing outbound dial.
      await voice.connectToNumber(normalizedPhone);
      setPlacing(false);
      onClose();
    } catch (err: unknown) {
      // A5.8 — the mint endpoint returns `{ error, code, message }` where
      // `code` carries Outbound's real reason (e.g. PROVIDER_NOT_ENTITLED).
      // Reading `error` first showed "Failed to mint Circle voice token",
      // which reads as a system fault when it is actually an entitlement
      // decision with a concrete remedy. Prefer the code's copy; the raw
      // code is still on the response and in server logs.
      const { message } = readEntitlementError(err);
      setError(message);
      setPlacing(false);
    }
  };

  // The resolved provider is an opaque id; it is compared, never printed.
  // It used to render verbatim ("Using: <uuid>").
  const resolvedIsAssigned =
    !!resolvedProviderId && !!assignedProviderId && resolvedProviderId === assignedProviderId;
  const fellBackFromAssigned =
    !!resolvedProviderId && !!assignedProviderId && resolvedProviderId !== assignedProviderId;

  const providerCaption = resolvedProviderId
    ? resolvedIsAssigned && assignedProviderHint
      ? `Using: ${assignedProviderHint}`
      : fellBackFromAssigned || !assignedProviderHint
        ? 'Using the workspace default voice provider.'
        : 'Voice provider ready.'
    : assignedProviderHint
      ? `Assigned: ${assignedProviderHint}`
      : 'Workspace default voice provider will be used.';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="circle-voice-call-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="circle-voice-call-modal"
    >
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-5">
        <h2
          id="circle-voice-call-title"
          className="text-lg font-medium text-gray-900"
        >
          Call from {circleName}
        </h2>
        <p className="mt-1 text-xs text-gray-500">
          {providerCaption}
        </p>
        {fellBackFromAssigned && (
            <p
              className="mt-1 text-xs text-amber-700"
              data-testid="circle-voice-call-fallback-notice"
            >
              Your assigned provider was not usable; the workspace default
              will be used.
            </p>
          )}

        <div className="mt-4">
          <label
            htmlFor="circle-voice-call-phone"
            className="block text-sm font-medium text-gray-700"
          >
            Phone number
          </label>
          <input
            id="circle-voice-call-phone"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="+15551234567"
            aria-invalid={phoneInput.length > 0 && !phoneIsValid}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="circle-voice-call-phone-input"
          />
          <p className="mt-1 text-xs text-gray-500">
            E.164 format required (starts with + and country code).
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
            data-testid="circle-voice-call-error"
          >
            {error}
          </div>
        )}

        {!voice.isReady && (
          <p
            className="mt-3 text-xs text-gray-500"
            data-testid="circle-voice-call-device-not-ready"
          >
            The browser calling device is not ready. Resolve any voice-device
            issues before placing a call.
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={placing}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            data-testid="circle-voice-call-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePlace}
            disabled={!canPlace}
            className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="circle-voice-call-confirm"
          >
            {placing ? 'Calling…' : 'Call'}
          </button>
        </div>
      </div>
    </div>
  );
}
